import { FC, useMemo, useCallback } from 'react';
import { css } from '@emotion/react';
import noop from 'lodash-es/noop';
import { TeamChatTypes } from '@frontend/api-team-chat';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { humanizeDateTime } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text, Dot, NakedButton } from '@frontend/design-system';
import { useTeamChatSelector } from '../../../../providers/team-chat.provider/team-chat.provider';
import { getUserFullName } from '../../../../utils';
import { MessageRegular } from '../../../conversation/message/message-regular';

interface ThreadMessageProps {
  channelName?: string;
  parentMessage: TeamChatTypes.Message;
  replyCount: number;
  latestReplies: TeamChatTypes.Message[];
  onClickThread: (parentMessage: TeamChatTypes.Message) => void;
  unreadCount: number;
  currentUserId?: string;
}

export const ThreadMessage: FC<ThreadMessageProps> = ({
  channelName = 'DM',
  parentMessage,
  replyCount,
  latestReplies,
  onClickThread,
  unreadCount,
  currentUserId,
}) => {
  const { t } = useTranslation('team-chat');
  const lastMessage = latestReplies[latestReplies.length - 1];

  const users = useTeamChatSelector((ctx) => ctx.users);
  const senderName = useMemo(() => {
    if (!lastMessage) return '';
    if (currentUserId && lastMessage.senderUserId === currentUserId) {
      return t('You');
    }
    return users.reduce<string>((acc, user) => {
      if (user.userID === lastMessage.senderUserId) {
        acc = getUserFullName(user.firstName, user.lastName);
      }
      return acc;
    }, '');
  }, [users, lastMessage, currentUserId, t]);

  const time = useMemo(() => {
    if (!lastMessage?.lastUpdated) return '';
    return humanizeDateTime(lastMessage.lastUpdated);
  }, [lastMessage]);

  const onClick = useCallback(() => onClickThread(parentMessage), [parentMessage, onClickThread]);

  return (
    <NakedButton css={styles.container} trackingId='team-chat-2.0-open-thread-from-threads-section' onClick={onClick}>
      <span css={styles.title}>
        {unreadCount > 0 && <Dot color='primary' css={styles.dot} />}
        <Text color='subdued'>{t('{{senderName}} replied in {{channelName}}', { channelName, senderName })}</Text>
        <Text color='subdued' size='medium' css={styles.time}>
          {time}
        </Text>
      </span>
      {/* Parent Message should be displayed here */}
      <MessageRegular
        message={parentMessage}
        isThread={true}
        onDelete={noop}
        isActionable={false}
        className='parent-message'
      />
      <div css={styles.reply}>
        <Icon name='thread' size={16} />
        <Text color='subdued' size='medium'>
          {t('{{count}} replies', { count: replyCount })}
          {unreadCount > 0 ? t(' ({{unreadCount}} unread)', { unreadCount }) : ''}
        </Text>
      </div>
      <MessageRegular
        message={lastMessage}
        onDelete={noop}
        isThread={true}
        isActionable={false}
        className='reply-message'
      />
    </NakedButton>
  );
};

const styles = {
  container: css`
    padding: ${theme.spacing(1, 2, 2)};
    width: 100%;
    text-align: start;
    border-bottom: 0.5px solid ${theme.colors.neutral20};

    :hover {
      background-color: ${theme.colors.neutral5};
    }

    .parent-message {
      padding: ${theme.spacing(2, 0, 1, 0)};
    }

    .reply-message {
      border: solid 1px ${theme.colors.neutral10};
      border-radius: ${theme.borderRadius.medium};
      background-color: ${theme.colors.white};
      margin-top: ${theme.spacing(1)};

      :hover {
        background-color: ${theme.colors.white};
      }
    }
  `,
  title: css`
    display: flex;
    gap: ${theme.spacing(1)};
    padding-bottom: ${theme.spacing(1)};
  `,
  dot: css`
    position: relative;
    flex-shrink: 0;
  `,
  reply: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(1, 0)};
  `,
  time: css`
    flex-shrink: 0;
  `,
};
